// ***** CollectionStore.js *****
// This store interacts with api endpoints defined in CmsResource.java
// This store provides data and function to RecipeCollection and WizardPlanCollection views: RecipeCollection.js, WizardPlanCollection.js

import {useEffect, useMemo, useState} from "react";
import axios from "axios";
import Constants from "../Constants";

const {collectionTypes} = Constants();
const isNotEmpty = (data) => ![null, undefined, ""].includes(data);

const emptyCollection = {
  id: null,
  cssClass: "",
  title: "",
  text: "",
  items: []
};

export default function useCollection(token, collectionType, id, enabled = true) {
  const canRequest = enabled && isNotEmpty(id)
    && [collectionTypes.RECIPE, collectionTypes.WIZARD_PLAN].includes(collectionType);
  const context = useMemo(() => ({token, collectionType, id, canRequest}),
    [token, collectionType, id, canRequest]);
  const [response, setResponse] = useState(null);

  useEffect(() => {
    let active = true;
    setResponse(null);
    if (context.canRequest) {
      let url = "";
      if (context.collectionType === collectionTypes.RECIPE) {
        url = `${process.env.REACT_APP_API}/cms/recipe-collections/${context.id}${isNotEmpty(context.token) ? "/personalized" : ""}`;
      } else if (context.collectionType === collectionTypes.WIZARD_PLAN) {
        url = `${process.env.REACT_APP_API}/cms/wizard-plan-collections/${context.id}${isNotEmpty(context.token) ? "/personalized" : ""}`;
      }
      const headers = {"Content-Type": "application/json"};
      if (isNotEmpty(context.token)) {
        headers.Authorization = `Bearer ${context.token}`;
      }
      axios({
        method: "GET",
        headers: headers,
        url: url,
      }).then(res => {
        if (active) {
          setResponse({context, status: "success", data: res.data});
        }
      }).catch(err => {
        if (active) {
          setResponse({context,
            status: err.response && [401, 403].includes(err.response.status) ? "denied" : "error"});
        }
      });
    }
    return () => { active = false; };
  }, [context]);

  // Match during render so an obsolete response cannot survive a context change.
  const current = canRequest && response && response.context === context ? response : null;
  const status = current ? current.status : canRequest ? "loading" : "idle";
  return {
    collection: status === "success" ? current.data : emptyCollection,
    status,
    loading: status === "loading",
    denied: status === "denied",
    error: status === "error"
  };
}
